export const debounce = (time: number) =>
  (fn: (...args: any[]) => void) => {
    let taskId = 0;
    return (...args: any[]) => {
      clearTimeout(taskId);
      taskId = setTimeout(() => {
        fn(...args);
      }, time);
    };
  };

export const filter = <IN, OUT>(condition: (i: IN) => boolean) =>
  (
    fn: (a: IN) => OUT,
  ) => {
    return (arg: IN) => {
      if (condition(arg)) {
        return fn(arg);
      }
    };
  };

export const switchMap = <IN, OUT>(fn: (arg: IN) => Promise<OUT>) => {
  let lastTaskId = 0;

  const exec = async (arg: IN) => {
    const id = ++lastTaskId;
    const result: OUT = await fn(arg);
    return { id, result, input: arg };
  };

  return (arg: IN): Promise<OUT> => {
    return new Promise((resolve) => {
      exec(arg).then(({ id, result }) => {
        if (id === lastTaskId) {
          resolve(result);
        }
      });
    });
  };
};

export const once = <T, A>(fn: (arg: A) => Promise<T>) => {
  let isFired = false;
  return async (args: A) => {
    if (isFired) {
      return;
    }
    isFired = true;
    return await fn(args);
  };
};

export const indexArr = <T, S extends keyof T>(
  arr: T[],
  key: S,
): Map<T[S], T> => {
  return new Map(arr.map((item) => [item[key], item]));
};

export const cache = <V>(defaultVal: V) => {
  const cache: Map<string, V> = new Map();
  const store = (k: string, v: V) => cache.set(k, v);
  let lastKey = "";
  const updateLast = (v: V) => cache.set(lastKey, v);
  return (
    view: string,
  ): {
    store: (k: string, v: V) => void;
    val: V;
    updateLast: (v: V) => void;
  } => {
    lastKey = view;
    if (cache.has(view)) {
      return { store, val: cache.get(view) as V, updateLast };
    } else {
      return { store, val: defaultVal, updateLast };
    }
  };
};
